import {
    ChatMessage,
    RiseAgentRecordLink,
    RiseAgentResourceLink,
} from "./rise-agent.types";

// Resolves which chart and which links belong to the answer being exported.
//
// Each response pushes a contiguous block of messages: the chart message
// immediately BEFORE the assistant text, then resource-links / record-links /
// download-prompt after it. Taking "the most recent chart in the chat" instead
// stapled an older answer's chart onto a newer answer (#2637), so this is scoped
// to the exported answer's own block and unit-tested.

export type ExportedAnswerBlock = {
    /**
     * Index of this answer's chart among ALL chart messages — i.e. its position
     * in the rendered chart-component list. -1 when this answer has no chart.
     */
    chartOrdinal: number;
    chartTitle: string;
    resourceLinks?: RiseAgentResourceLink[];
    recordLinks?: RiseAgentRecordLink[];
};

export function resolveExportedAnswerBlock(
    messages: ChatMessage[],
    analysisIndex: number,
    hasAnalysis: boolean,
): ExportedAnswerBlock {
    const empty: ExportedAnswerBlock = { chartOrdinal: -1, chartTitle: "" };
    if (!hasAnalysis || analysisIndex < 0 || analysisIndex >= messages.length) {
        return empty;
    }

    // The chart for this answer, if any, sits immediately before it.
    let chartOrdinal = -1;
    let chartTitle = "";
    if (analysisIndex > 0 && messages[analysisIndex - 1]?.type === "chart") {
        const chartMsgIndex = analysisIndex - 1;
        chartOrdinal = messages
            .slice(0, chartMsgIndex)
            .filter((m) => m.type === "chart").length;
        chartTitle = messages[chartMsgIndex].chartData?.title ?? "";
    }

    // Links emitted with this answer live between it and the next user message.
    const nextUserOffset = messages
        .slice(analysisIndex + 1)
        .findIndex((m) => m.role === "user");
    const blockEnd =
        nextUserOffset === -1
            ? messages.length
            : analysisIndex + 1 + nextUserOffset;
    const block = messages.slice(analysisIndex + 1, blockEnd);

    return {
        chartOrdinal,
        chartTitle,
        resourceLinks: block.find((m) => m.type === "resource-links")
            ?.resourceLinks,
        recordLinks: block.find((m) => m.type === "record-links")?.recordLinks,
    };
}
